/**
 * A task's words as `board_read` hands them out (audit cards MCP 7 + MCP 1).
 *
 * The store keeps a task's description as editor HTML (`descriptionHtml`) and,
 * on team boards, as the editor's block snapshot too (`descriptionLexical`);
 * a rich comment keeps `bodyHtml` beside its plain `body`. None of those leave
 * raw any more:
 *
 *  - `description` — the description in the requested `format`: markdown
 *    (default), html (the editor HTML minus image keys) or text;
 *  - each comment's `body` — the same, from `bodyHtml` when the comment is
 *    rich, else its plain text;
 *  - `descriptionHtml`, `bodyHtml` and every `*Lexical` field are dropped. The
 *    block snapshot is the editor's internal format (documents never return
 *    theirs either) and its image nodes carry the decryption key as `k`.
 *
 * Markdown and text go through `renderBody`, the documents' renderer, so a
 * task reads exactly like a document; an image is a "🖼 Image: name" line.
 */
import { renderBody } from './markdown';

export const BOARD_FORMATS = Object.freeze(['markdown', 'html', 'text']);

/** An unknown or missing format reads as markdown, as `document_read` does. */
export const boardFormatOf = (format) => (BOARD_FORMATS.includes(format) ? format : 'markdown');

const escapeHtml = (s) =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Every field except the editor snapshots — `*Lexical`, and their compact
 * stored form `*Blocks` (`descriptionBlocks`, taskDescriptionCodec.js), whose
 * image nodes carry the key as `k` just the same.
 */
function withoutLexical(obj) {
  const out = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!/(Lexical|Blocks)$/.test(key)) out[key] = value;
  }
  return out;
}

function commentText(comment, format) {
  const { bodyHtml, ...rest } = withoutLexical(comment || {});
  let body;
  if (bodyHtml) body = renderBody(bodyHtml, format);
  else if (format === 'html') {
    body = String(rest.body ?? '')
      .split('\n')
      .map((line) => `<p>${escapeHtml(line)}</p>`)
      .join('');
  } else body = String(rest.body ?? '');
  return { ...rest, body };
}

/** One task, its description and (when present) comments in `format`. */
export function taskText(task, format) {
  const { descriptionHtml, ...rest } = withoutLexical(task || {});
  const out = { ...rest, description: renderBody(descriptionHtml || '', format) };
  if (Array.isArray(task?.comments)) out.comments = task.comments.map((c) => commentText(c, format));
  return out;
}
